/**
 * 70.2 (DECISIONS 338: "All ten", L1 + T1, W0): the ten normal trees redrawn, as design_sheets/trees.html drew them —
 * the kit's seven (biomeKit `buildTree` builds them here) and the three classic species that grow at the normal size
 * (spire pine, canopy oak, frond palm: `treeModel` builds them here; the classic flora builder keeps its giants).
 *
 * The kit's shapes, which the forms (treeForms.ts) and the fits (treeShapeFit.ts: the sim's wood and foliage) read: the
 * crown is icosahedra and cones (the palm's blades planes), the wood cylinders and the bottle tree's lathe. A trunk in
 * several lengths is marked (`userData.trunk`) so the forms cut and scorch its foot and never snap it as a limb.
 *
 * DECISIONS 339 (the owner: "the trees that are bent have open gaps in the space between the bend and the base"): a bent
 * trunk's lengths are mitred — each pair meets on the plane halfway between their directions, so the two ends are one
 * ring and nothing opens at the bend — and its foot is cut flat on the ground, so a leaning trunk stands on it.
 *
 * L1: `bakeTreeLight` darkens a crown at its foot and middle and lightens it at the top, and darkens the wood toward the
 * ground, in the vertex colours (the stands merge one geometry; no cost a frame). T1 is the stands' material (a tint an
 * instance, treeStands.ts `standsMaterial`).
 */
import * as THREE from 'three';
import { markShared } from './meshBake';
import type { TreeColors } from './biomeKit';

export type TreeFamily = 'puffElm' | 'canopyOak' | 'paleBirch' | 'stackFir' | 'spirePine' | 'lanternFir' | 'windPine' | 'bottleTree' | 'frondPalm' | 'snag'
  // 70.5 (DECISIONS 350: "All ten"): the dressing sheet's ten new families (design_sheets/dressing.html)
  | 'drapeWillow' | 'parasolThorn' | 'pillarRedwood' | 'treeFern' | 'stiltMangrove' | 'dragonBlood' | 'candleCypress' | 'goldLarch' | 'krummholz' | 'ghostCap'
  // 421 (DECISIONS 420/421: "I like the american elm … Japanese maple is good. Spreading oak is good. Round oak is good. Pin oak is great"):
  // the species sheet's five (design_sheets/tree_species.html) — variation within the puff elm's and the canopy oak's families
  | 'vaseElm' | 'japaneseMaple' | 'spreadingOak' | 'roundOak' | 'pinOak'
  // 423 (the owner: "all 3 new ones are good"): the three redrawn for a silhouette difference beside the birch, the drape willow and the puff elm
  | 'quakingAspen' | 'weepingWillow' | 'chestnut'
  // 430 (DECISIONS 425 (3), 430: "Jungle rainforest thing looks good"): the rainforest sheet's three (design_sheets/rainforest.html) — the
  // mix of the woods field's warm wet closed regime (core/woodsDensity.ts WOODS_RAINFOREST), with the kit's palm, tree fern and mangrove
  | 'kapok' | 'stranglerFig' | 'bambooClump';
export const NEW_TREE_FAMILIES: readonly TreeFamily[] = ['drapeWillow', 'parasolThorn', 'pillarRedwood', 'treeFern', 'stiltMangrove', 'dragonBlood', 'candleCypress', 'goldLarch', 'krummholz', 'ghostCap'];
/** 421: the species in play, each a variation within a family it stands beside (the elm's and the maple's the puff elm, the oaks' the canopy oak); normal size only, as 70.5's. */
export const SPECIES_FAMILIES: readonly TreeFamily[] = ['vaseElm', 'japaneseMaple', 'spreadingOak', 'roundOak', 'pinOak', 'quakingAspen', 'weepingWillow', 'chestnut'];
/** 430: the rainforest's own three (the regime's mix draws them; no biome's dressing does); normal size only. */
export const RAINFOREST_FAMILIES: readonly TreeFamily[] = ['kapok', 'stranglerFig', 'bambooClump'];
export const TREE_FAMILIES: readonly TreeFamily[] = ['puffElm', 'canopyOak', 'paleBirch', 'stackFir', 'spirePine', 'lanternFir', 'windPine', 'bottleTree', 'frondPalm', 'snag', ...NEW_TREE_FAMILIES, ...SPECIES_FAMILIES, ...RAINFOREST_FAMILIES];
/**
 * 430: the families drawn in their own colours, whatever biome they stand in (their builds take no `TreeColors`): one prototype
 * each per variant, not one per biome — the stands key their prototypes on it (rendering/treeStands.ts). The rainforest's mix
 * stands in every biome the closed regime fills, and keyed by biome it baked the same six models again for each (339
 * prototypes in a viridis rainforest against 214 in today's jungle: the chunks' build 644 → 390 ms).
 */
export const OWN_COLOUR_FAMILIES: ReadonlySet<string> = new Set<TreeFamily>(['spirePine', 'canopyOak', 'frondPalm', 'drapeWillow', 'parasolThorn', 'pillarRedwood', 'treeFern', 'stiltMangrove', 'dragonBlood', 'candleCypress', 'goldLarch', 'krummholz', 'kapok', 'stranglerFig', 'bambooClump']);
export const isTreeFamily = (t: string): t is TreeFamily => (TREE_FAMILIES as readonly string[]).includes(t);

// ---------------------------------------------------------------------------
// Parts
// ---------------------------------------------------------------------------

const matCache = new Map<string, THREE.MeshLambertMaterial>();
export function mat(color: string, glow = 0): THREE.MeshLambertMaterial {
  const key = `${color}:${glow}`;
  let m = matCache.get(key);
  if (!m) {
    m = markShared(new THREE.MeshLambertMaterial({ color, flatShading: true, ...(glow > 0 ? { emissive: new THREE.Color(color), emissiveIntensity: glow } : {}) }));
    matCache.set(key, m);
  }
  return m;
}
/** mulberry32 (biomeKit's): the same seed draws the same tree. */
export function rng(seed: number): () => number {
  let s = (seed * 2654435761) >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
export function lumpy(geo: THREE.BufferGeometry, amount: number, offset: number): THREE.BufferGeometry {
  const pos = geo.attributes.position as THREE.BufferAttribute;
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i), y = pos.getY(i), z = pos.getZ(i);
    const h = Math.sin(x * 127.1 + y * 311.7 + z * 74.7 + offset) * 43758.5453;
    const j = 1 + amount * ((h - Math.floor(h)) - 0.5) * 2;
    pos.setXYZ(i, x * j, y * j, z * j);
  }
  geo.computeVertexNormals();
  return geo;
}
export function blob(parent: THREE.Object3D, r: number, color: string, x: number, y: number, z: number, sy = 1, detail = 1, off = 0, glow = 0): THREE.Mesh {
  const m = new THREE.Mesh(lumpy(new THREE.IcosahedronGeometry(r, detail), 0.12, off), mat(color, glow));
  m.position.set(x, y, z); m.scale.y = sy;
  parent.add(m);
  return m;
}
export type V3 = [number, number, number];
const UP = new THREE.Vector3(0, 1, 0);
/**
 * A cylinder from `a` (radius `ra`) to `b` (radius `rb`): a trunk's length, a limb, a twig. `cut` mitres an end: its ring
 * laid on the plane through that end with the given normal (a bend's bisector, the ground's up) instead of square across.
 */
export function seg(parent: THREE.Object3D, ra: number, rb: number, a: V3, b: V3, color: string, sides = 6, cut?: { a?: V3; b?: V3 }): THREE.Mesh {
  const A = new THREE.Vector3(...a), B = new THREE.Vector3(...b), d = B.clone().sub(A), len = d.length();
  const geo = new THREE.CylinderGeometry(rb, ra, len, sides);
  const m = new THREE.Mesh(geo, mat(color));
  m.position.copy(A).addScaledVector(d, 0.5);
  m.quaternion.setFromUnitVectors(UP, d.normalize());
  if (cut) {
    const inv = m.quaternion.clone().invert(), pos = geo.attributes.position as THREE.BufferAttribute;
    for (const [end, n] of [[-1, cut.a], [1, cut.b]] as const) {
      if (!n) continue;
      const l = new THREE.Vector3(...n).normalize().applyQuaternion(inv);   // the plane's normal in the cylinder's frame
      if (l.y < 0.2) continue;                                                // (a bend past ~78°: square across)
      for (let i = 0; i < pos.count; i++) {
        const y = pos.getY(i);
        if (Math.abs(y - end * len / 2) > 1e-6) continue;   // (the end's ring and its cap: float32 in the buffer)
        pos.setY(i, y - (l.x * pos.getX(i) + l.z * pos.getZ(i)) / l.y);
      }
    }
    geo.computeVertexNormals();
  }
  parent.add(m);
  return m;
}
/**
 * A trunk through `pts` (its foot first, on the ground), each length's radii at its foot and top: one length a pair of
 * points, mitred at every bend and cut flat at the foot (DECISIONS 339), each length marked as the trunk (unless `trunk`
 * is false: a mangrove's prop root, bent the same way).
 */
function trunkChain(parent: THREE.Object3D, pts: V3[], radii: Array<[number, number]>, color: (i: number) => string, sides = 6, trunk = true): void {
  const dir = (i: number) => new THREE.Vector3(pts[i + 1][0] - pts[i][0], pts[i + 1][1] - pts[i][1], pts[i + 1][2] - pts[i][2]).normalize();
  const bisect = (i: number): V3 => { const n = dir(i - 1).add(dir(i)).normalize(); return [n.x, n.y, n.z]; };
  for (let i = 0; i + 1 < pts.length; i++) {
    const m = seg(parent, radii[i][0], radii[i][1], pts[i], pts[i + 1], color(i), sides, {
      a: i === 0 ? (pts[0][1] === 0 ? [0, 1, 0] : undefined) : bisect(i),
      b: i + 2 < pts.length ? bisect(i + 1) : undefined,
    });
    if (trunk) m.userData.trunk = true;
  }
}
function cone(parent: THREE.Object3D, r: number, h: number, color: string, y: number, sides = 7, flip = false): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.ConeGeometry(r, h, sides), mat(color));
  m.position.y = y + h / 2;
  if (flip) m.rotation.x = Math.PI;
  parent.add(m);
  return m;
}
/** A cone standing at (x, y, z) (70.5's families place theirs off the axis). */
function coneAt(parent: THREE.Object3D, r: number, h: number, color: string, x: number, y: number, z: number, sides = 7, flip = false): THREE.Mesh {
  const m = cone(parent, r, h, color, y, sides, flip);
  m.position.x = x; m.position.z = z;
  return m;
}
function lathe(parent: THREE.Object3D, pts: Array<[number, number]>, color: string, sides: number): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.LatheGeometry(pts.map(([x, y]) => new THREE.Vector2(x, y)), sides), mat(color));
  parent.add(m);
  return m;
}
/** A leaf blade drawn from both faces (the stands' material draws a face's front only). */
export function leafBlade(parent: THREE.Object3D, w: number, l: number, color: string, x: number, y: number, z: number, rx = 0, rz = 0): void {
  const m = mat(color), q = new THREE.Mesh(new THREE.PlaneGeometry(w, l), m);
  q.position.set(x, y, z); q.rotation.set(rx, 0, rz, 'YXZ');
  parent.add(q);
  const back = new THREE.Mesh(q.geometry, m); back.rotation.y = Math.PI; q.add(back);
}
function box(parent: THREE.Object3D, w: number, h: number, d: number, color: string, x: number, y: number, z: number, ry = 0): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat(color));
  m.position.set(x, y, z); m.rotation.y = ry;
  parent.add(m);
  return m;
}
export const shade = (c: string, k: number) => '#' + new THREE.Color(c).multiplyScalar(k).getHexString();
const add = (a: V3, b: V3, k = 1): V3 => [a[0] + b[0] * k, a[1] + b[1] * k, a[2] + b[2] * k];
const dirOf = (a: number, up: number, out = 1): V3 => { const l = Math.hypot(out, up); return [Math.cos(a) * out / l, up / l, Math.sin(a) * out / l]; };
/** The pale wood of a break (treeForms TREE_FORM.wood). */
const BREAK_WOOD = '#cdb68a';

// ---------------------------------------------------------------------------
// The ten (model units: ~0.25–0.4 tall, base at y = 0); each returns the parts that sway (a giant's animation)
// ---------------------------------------------------------------------------

/**
 * DECISIONS 393 (the owner: "intra-species variation of trees"): a family's main count — its limbs, tiers, whorls, fronds or
 * roots — as `base` moved by the tree's count step (`step` a step; never under `min`). With no step (the stands' call) it is
 * `base`, and every count and place is as before, bit for bit.
 */
type Count = (base: number, min?: number, step?: number) => number;
type Build = (g: THREE.Group, c: TreeColors, r: () => number, n: Count) => THREE.Object3D[];
const TAU = Math.PI * 2;

/** Puff elm: a short trunk forking into three limbs under a domed crown of seven lumps. */
const puffElm: Build = (g, c, r, n) => {
  seg(g, 0.02, 0.014, [0, 0, 0], [0, 0.1, 0], c.bark, 7).userData.trunk = true;   // (the old elm's foot: 0.022 left a bot's corner lean grazing it)
  // a leader up into the crown's middle: the middle lumps hang from it, so snapped limbs (70.1's forms) take the outer ones only
  seg(g, 0.01, 0.007, [0, 0.1, 0], [0, 0.2, 0], c.bark, 5).userData.trunk = true;
  const crown = new THREE.Group(); g.add(crown);
  const limbs = n(3, 2), lumps = n(4, 3);
  for (let k = 0; k < limbs; k++) {
    const a = (limbs === 3 ? k * 2.1 : k * TAU / limbs) + r() * 0.5, end: V3 = [Math.cos(a) * 0.048, 0.165 + r() * 0.02, Math.sin(a) * 0.048];
    seg(g, 0.01, 0.006, [0, 0.092, 0], end, c.bark, 5);
  }
  for (let k = 0; k < lumps; k++) {
    const a = (lumps === 4 ? k * Math.PI / 2 : k * TAU / lumps) + r() * 0.6, rr = 0.06 + r() * 0.012;
    blob(crown, 0.05 + r() * 0.01, k % 2 ? c.leafDark : c.leaf, Math.cos(a) * rr, 0.165 + r() * 0.015, Math.sin(a) * rr, 0.82, k < 2 ? 1 : 0, r() * 9);
  }
  blob(crown, 0.062, c.leaf, 0.022, 0.215, -0.01, 0.85, 1, r() * 9);
  blob(crown, 0.055, c.leafDark, -0.028, 0.205, 0.02, 0.85, 1, r() * 9);
  blob(crown, 0.04, c.leaf, 0.0, 0.262, 0.0, 0.85, 1, r() * 9);
  return [crown];
};

/** Pale birch: a slender trunk with a kink, dark lenticels, the crown hanging in long drooping clumps off short limbs. */
const paleBirch: Build = (g, c, r, n) => {
  const bark = c.accent ?? '#d8d4c8';
  const p0: V3 = [0, 0, 0], p1: V3 = [0.004, 0.17, 0], p2: V3 = [-0.003, 0.34, 0.003];
  trunkChain(g, [p0, p1, p2], [[0.0125, 0.0095], [0.0095, 0.0055]], () => bark, 6);
  for (let i = 0; i < 8; i++) {
    const t = 0.08 + i * 0.105 + r() * 0.03, y = t * 0.34, a = r() * Math.PI * 2, rad = 0.0125 - 0.007 * t;
    const cx = t < 0.5 ? 0.004 * t * 2 : 0.004 - 0.007 * (t - 0.5) * 2;
    box(g, 0.009, 0.003, 0.004, '#3e3a36', cx + Math.cos(a) * rad, y, Math.sin(a) * rad, -a + Math.PI / 2);
  }
  const crown = new THREE.Group(); g.add(crown);
  const clumps = n(6, 3);
  for (let k = 0; k < clumps; k++) {
    const t = 0.5 + (clumps === 6 ? k * 0.085 : k * 0.51 / clumps), y = t * 0.34, a = k * 2.4 + r() * 0.6, out = 0.03 + r() * 0.012;
    const at: V3 = [0, y, 0], end = add(at, dirOf(a, 0.55), out * 1.15);
    seg(crown, 0.0035, 0.0022, at, end, bark, 4);
    const col = k % 2 ? c.leafDark : c.leaf, rr = 0.022 + r() * 0.005;
    blob(crown, rr, col, end[0], end[1] - 0.008, end[2], 1.4, 0, r() * 9);
    blob(crown, rr * 0.78, col, end[0] + Math.cos(a) * 0.012, end[1] - 0.036, end[2] + Math.sin(a) * 0.012, 1.5, 0, r() * 9);   // (the clump hangs: a second lump below and out)
  }
  blob(crown, 0.022, c.leaf, p2[0], p2[1] - 0.006, p2[2], 1.35, 0, r() * 9);
  return [crown];
};

/** Stack fir: five drooping tiers, each with its shadowed underside, a leader at the top; the lantern fir's beads. */
function stackFir(g: THREE.Group, c: TreeColors, r: () => number, beads = false, n: Count = b => b): THREE.Object3D[] {
  seg(g, 0.017, 0.01, [0, 0, 0], [0, 0.09, 0], c.bark, 6).userData.trunk = true;
  const crown = new THREE.Group(); g.add(crown);
  const under = shade(c.leafDark, 0.8), tiers = n(5, 3);
  let top = 0;
  for (let i = 0; i < tiers; i++) {
    const t = i / (tiers - 1), rr = 0.105 * (1 - t * 0.74) + 0.006, h = 0.092 * (1 - t * 0.3), y = 0.04 + (tiers === 5 ? i * 0.046 : i * 0.184 / (tiers - 1));
    const tier = new THREE.Group(); tier.position.set((r() - 0.5) * 0.006, y, (r() - 0.5) * 0.006); tier.rotation.set((r() - 0.5) * 0.08, r() * Math.PI, (r() - 0.5) * 0.08); crown.add(tier);
    cone(tier, rr, h, i % 2 ? c.leafDark : c.leaf, 0, 7);
    cone(tier, rr * 0.95, h * 0.22, under, h * 0.1 - h * 0.11, 7, true);
    if (beads) {
      // the lantern fir: three lit droplets under each tier's edge (67.31), and a string of two from the tier's lowest point
      for (let k = 0; k < 3; k++) {
        const a = (k / 3) * Math.PI * 2 + r() * 2;
        const b = new THREE.Mesh(new THREE.IcosahedronGeometry(0.0075, 0), mat(c.accent ?? '#e6d89a', 1.1));
        b.scale.y = 1.7; b.position.set(Math.cos(a) * rr * 0.8, -0.008, Math.sin(a) * rr * 0.8); tier.add(b);
      }
      const a = r() * Math.PI * 2;
      for (let k = 0; k < (i > 0 ? 2 : 0); k++) {   // (not under the lowest tier: it would reach the ground)
        const b = new THREE.Mesh(new THREE.IcosahedronGeometry(0.0055, 0), mat(c.accent ?? '#e6d89a', 1.25));
        b.scale.y = 1.5; b.position.set(Math.cos(a) * rr * 0.6, -0.022 - k * 0.016, Math.sin(a) * rr * 0.6); tier.add(b);
      }
    }
    top = y + h;
  }
  cone(crown, 0.01, 0.036, c.leaf, top - 0.01, 5);
  return [crown];
}

/** Spire pine: eight narrow tiers alternating their lean, a leader, four dead lower limbs (its own greens and bark); each tier sways. */
const spirePine: Build = (g, _c, r, n) => {
  const bark = '#5a4030', barkDk = '#45321f', lean = (r() - 0.5) * 0.14;
  seg(g, 0.026, 0.012, [0, 0, 0], [0, 0.03, 0], barkDk, 6).userData.trunk = true;
  seg(g, 0.013, 0.005, [0, 0.02, 0], [-Math.sin(lean) * 0.27, 0.29, 0], bark, 6).userData.trunk = true;   // (it starts inside the foot: no bend to open)
  const crown = new THREE.Group(); g.add(crown);
  const tones = ['#2a5a30', '#2d6335', '#306838', '#286030'], sway: THREE.Object3D[] = [];
  let y = 0.065;
  const tiers = n(8, 5);
  for (let i = 0; i < tiers; i++) {
    const t = i / (tiers - 1), rr = 0.072 * (1 - t * 0.8) + 0.006, h = 0.07 - t * 0.022;
    const tier = new THREE.Group(); tier.position.set(-Math.sin(lean) * y + (r() - 0.5) * 0.006, y, (r() - 0.5) * 0.006); tier.rotation.set((i % 2 ? 1 : -1) * 0.05, i * 0.45, (r() - 0.5) * 0.05); crown.add(tier); sway.push(tier);
    cone(tier, rr, h, tones[i % 4], 0, 7);
    cone(tier, rr * 0.94, h * 0.3, '#1e4424', h * 0.14 - h * 0.15, 7, true);
    y += h * (tiers === 8 ? 0.52 : 0.52 * 8 / tiers);
  }
  const tier = new THREE.Group(); tier.position.set(-Math.sin(lean) * y, y - 0.01, 0); crown.add(tier); sway.push(tier);
  cone(tier, 0.008, 0.045, tones[1], 0, 5);
  for (let i = 0; i < 4; i++) {
    const a = r() * Math.PI * 2, at: V3 = [Math.cos(a) * 0.008, 0.045 + i * 0.011, Math.sin(a) * 0.008];
    seg(g, 0.003, 0.0015, at, add(at, dirOf(a, -0.25), 0.05), barkDk, 4);
  }
  return sway;
};

/** Wind pine: a crooked trunk bending further with the wind, an umbrella crown in layered pads, a side limb with its own. */
const windPine: Build = (g, c, r) => {
  const lean = 0.25 + r() * 0.15;
  const p1: V3 = [Math.sin(lean * 0.55) * 0.11, Math.cos(lean * 0.55) * 0.11, 0], p2 = add(p1, [Math.sin(lean * 1.7) * 0.105, Math.cos(lean * 1.7) * 0.105, 0]);
  trunkChain(g, [[0, 0, 0], p1, p2], [[0.016, 0.012], [0.012, 0.008]], () => c.bark, 6);
  const top = new THREE.Group(); top.position.set(...p2); g.add(top);
  blob(top, 0.09, c.leafDark, 0.02, 0.008, 0, 0.3, 1, r() * 9);
  blob(top, 0.064, c.leaf, -0.035, 0.028, 0.02, 0.36, 1, r() * 9);
  blob(top, 0.046, c.leaf, 0.05, 0.03, -0.025, 0.36, 0, r() * 9);
  const side = add(p1, dirOf(Math.PI + 0.3, 0.55), 0.085);
  seg(g, 0.006, 0.004, add(p1, [0, -0.01, 0]), side, c.bark, 5);
  const pad = blob(g, 0.04, c.leafDark, side[0] - 0.01, side[1] + 0.006, side[2], 0.34, 0, r() * 9);
  return [top, pad];
};

/** Bottle tree: a full bottle (nine sides), five limbs, the tufts flat like an acacia's. */
const bottleTree: Build = (g, c, r, n) => {
  const pts = [[0.024, 0], [0.05, 0.022], [0.056, 0.065], [0.05, 0.11], [0.032, 0.148], [0.017, 0.176], [0.013, 0.2]].map(([x, y]) => new THREE.Vector2(x, y));
  const body = new THREE.Mesh(new THREE.LatheGeometry(pts, 9), mat(c.bark)); body.userData.trunk = true;
  g.add(body);
  const crown = new THREE.Group(); crown.position.y = 0.198; g.add(crown);
  const limbs = n(5, 3);
  for (let k = 0; k < limbs; k++) {
    const a = (k / limbs) * Math.PI * 2 + r() * 0.5, len = 0.05 + r() * 0.015, end: V3 = add([0, 0, 0], dirOf(a, 0.75), len);
    seg(crown, 0.0055, 0.0035, [0, 0, 0], end, c.bark, 5);
    blob(crown, 0.03 + r() * 0.006, k % 2 ? c.leaf : c.leafDark, end[0], end[1] + 0.006, end[2], 0.38, 0, r() * 9);
  }
  blob(crown, 0.026, c.leaf, 0, 0.03, 0, 0.42, 0, r() * 9);
  return [crown];
};

/** Canopy oak: a gnarled trunk on four buttress roots, four limbs spreading wide, a broad flat crown (its own greens). */
const canopyOak: Build = (g, _c, r, n) => {
  const bark = '#5a4028';
  for (let i = 0; i < 4; i++) { const a = (i / 4) * Math.PI * 2 + 0.4; seg(g, 0.011, 0.005, [Math.cos(a) * 0.03, 0, Math.sin(a) * 0.03], [Math.cos(a) * 0.012, 0.045, Math.sin(a) * 0.012], bark, 5); }
  trunkChain(g, [[0, 0, 0], [0.006, 0.07, 0.002], [-0.002, 0.115, 0.004]], [[0.022, 0.017], [0.017, 0.013]], () => bark, 7);
  const lower = ['#356530', '#3a6838'], upper = ['#3d7040', '#408a40', '#4a9448'];
  const pick = (a: string[]) => a[Math.floor(r() * a.length)];
  const sway: THREE.Object3D[] = [];
  const limbs = n(4, 3);
  for (let i = 0; i < limbs; i++) {
    const a = (i / limbs) * Math.PI * 2 + (r() - 0.5) * 0.5, spread = 0.065 + r() * 0.02, end: V3 = [Math.cos(a) * spread, 0.165 + r() * 0.02, Math.sin(a) * spread];
    seg(g, 0.009, 0.005, [0, 0.105, 0], end, bark, 5);
    const cluster = new THREE.Group(); cluster.position.set(...end); g.add(cluster); sway.push(cluster);
    blob(cluster, 0.04 + r() * 0.008, pick(upper), 0, 0.016, 0, 0.66, 1, r() * 9);
    blob(cluster, 0.032, pick(lower), Math.cos(a) * 0.028, -0.006, Math.sin(a) * 0.028, 0.62, 0, r() * 9);
    blob(cluster, 0.03, pick(lower), -Math.sin(a) * 0.026, -0.004, Math.cos(a) * 0.026, 0.62, 0, r() * 9);
  }
  const crown = new THREE.Group(); crown.position.set(0, 0.215, 0); g.add(crown); sway.push(crown);
  blob(crown, 0.05, pick(upper), 0.012, 0, -0.008, 0.62, 1, r() * 9);
  blob(crown, 0.042, pick(upper), -0.02, 0.008, 0.016, 0.62, 1, r() * 9);
  return sway;
};

/**
 * Frond palm: the trunk's rings as alternating bands, nine fronds each in two blades that rise then droop, drawn from
 * both sides (the stands' material draws a face's front only), coconuts under the hub, one dead frond hanging.
 */
const frondPalm: Build = (g, _c, r, n) => {
  const segColors = ['#6a5838', '#55432c'], segH = 0.026, segs = 8, dir = r() * Math.PI * 2;
  let cx = 0, cy = 0, lean = (r() - 0.5) * 0.04;
  const pts: V3[] = [[0, 0, 0]], radii: Array<[number, number]> = [];
  for (let i = 0; i < segs; i++) {
    lean += 0.055;
    cx += Math.sin(lean) * segH; cy += Math.cos(lean) * segH;
    pts.push([Math.cos(dir) * cx, cy, Math.sin(dir) * cx]);
    radii.push([0.0085 - i * 0.0006 + 0.0014, 0.0085 - i * 0.0006]);   // (each band a little proud of the one above: the rings)
  }
  trunkChain(g, pts, radii, i => segColors[i % 2], 6);
  const [hx, hy, hz] = pts[segs];
  blob(g, 0.013, '#4a6030', hx, hy, hz, 0.7, 0, 1);
  for (let i = 0; i < 4; i++) { const a = i * 1.6; blob(g, 0.0055, '#5a4a30', hx + Math.cos(a) * 0.01, hy - 0.009, hz + Math.sin(a) * 0.01, 1, 0, i); }
  const greens = ['#3a7838', '#408040', '#357030', '#458a3a', '#3a7535'];
  const blade = (parent: THREE.Object3D, w: number, l: number, color: string, x: number, y: number, z: number, rx: number, rz: number) => {
    const m = mat(color), q = new THREE.Mesh(new THREE.PlaneGeometry(w, l), m);
    q.position.set(x, y, z); q.rotation.set(rx, 0, rz, 'YXZ');
    parent.add(q);
    const back = new THREE.Mesh(q.geometry, m); back.rotation.y = Math.PI; q.add(back);   // (its back face)
  };
  const fronds = n(9, 5, 2), sway: THREE.Object3D[] = [];
  for (let i = 0; i <= fronds; i++) {
    const dead = i === fronds, frond = new THREE.Group();
    frond.position.set(hx, hy, hz); frond.rotation.order = 'YXZ';
    frond.rotation.y = (i / fronds) * Math.PI * 2 + (r() - 0.5) * 0.3;
    frond.rotation.x = dead ? 2.0 + r() * 0.2 : 0.95 + r() * 0.4;   // (out toward level, the blades drooping beyond)
    const col = dead ? '#8a7040' : greens[i % greens.length];
    seg(frond, 0.0032, 0.0016, [0, 0, 0], [0, 0.075, 0], dead ? '#6a5030' : '#5a5030', 4);
    for (const side of [-1, 1]) {
      blade(frond, dead ? 0.01 : 0.015, 0.042, col, side * 0.0065, 0.026, 0.002, 0.12, side * 0.6);
      if (!dead) blade(frond, 0.012, 0.036, shade(col, 0.9), side * 0.0055, 0.06, 0.012, 0.75, side * 0.5);   // (the dead frond hangs folded: its inner blade only)
    }
    g.add(frond); sway.push(frond);
  }
  return sway;
};

/** Snag: a kinked dead trunk with a splintered top, three bare limbs (two forked), a pale scar and two shelf fungi. */
const snag: Build = (g, c, r, n) => {
  const lean = (r() - 0.5) * 0.08, p1: V3 = [Math.sin(lean) * 0.18, 0.18, 0], p2 = add(p1, [-0.012, 0.085, 0.006]);
  trunkChain(g, [[0, 0, 0], p1, p2], [[0.018, 0.012], [0.012, 0.008]], () => c.bark, 6);
  const old = shade(BREAK_WOOD, 0.72);
  for (let k = 0; k < 3; k++) {
    const a = (k / 3) * Math.PI * 2 + r(), h = 0.012 + r() * 0.014;
    const s = box(g, 0.0045, h, 0.003, k % 2 ? old : c.bark, p2[0] + Math.cos(a) * 0.004, p2[1] + h / 2 - 0.002, p2[2] + Math.sin(a) * 0.004, -a);
    s.rotation.z = (r() - 0.5) * 0.4;
  }
  box(g, 0.003, 0.05, 0.011, old, Math.sin(lean) * 0.11 + 0.0152, 0.11, 0, 0);   // (on the trunk's face: it is 0.0147 across there)
  const limbs = n(3, 1);
  for (let k = 0; k < limbs; k++) {
    const a = k * 2.2 + r() * 0.6, y = 0.1 + (limbs === 3 ? k * 0.045 : k * 0.09 / Math.max(1, limbs - 1)), at: V3 = [Math.sin(lean) * y, y, 0], end = add(at, dirOf(a, 0.6 + r() * 0.5), 0.055 + r() * 0.03);
    seg(g, 0.005, 0.003, at, end, c.bark, 5);
    if (k < 2) seg(g, 0.0028, 0.0015, end, add(end, dirOf(a + 0.8, 1.1), 0.025), c.bark, 4);
  }
  for (let k = 0; k < 2; k++) {
    const a = r() * Math.PI * 2, y = 0.05 + k * 0.035, f = new THREE.Mesh(new THREE.CylinderGeometry(0.012, 0.008, 0.0045, 7), mat('#a89a7a'));
    f.position.set(Math.sin(lean) * y + Math.cos(a) * 0.02, y, Math.sin(a) * 0.02); g.add(f);
  }
  return [];
};

// ---------------------------------------------------------------------------
// 70.5 (DECISIONS 350): the dressing sheet's ten, as it drew them (design_sheets/dressing.ts) — their own colours but
// the ghost cap's spots (the biome's accent); none is a giant, so none sways
// ---------------------------------------------------------------------------

/** Drape willow: a short forked trunk under a dome that falls in curtains of hanging strands. */
const drapeWillow: Build = (g, _c, r, n) => {
  const bark = '#5e5444', leaf = '#8aa25a', dark = '#6a8244', p1: V3 = [0.01, 0.07, 0], p2: V3 = [0, 0.11, 0.01];
  trunkChain(g, [[0, 0, 0], p1, p2], [[0.024, 0.019], [0.019, 0.015]], () => bark, 7);
  const limbs = n(3, 2), lumps = n(4, 3);
  for (let k = 0; k < limbs; k++) { const a = (limbs === 3 ? k * 2.1 : k * TAU / limbs) + r() * 0.4; seg(g, 0.011, 0.007, [0, 0.1, 0], [Math.cos(a) * 0.05, 0.17, Math.sin(a) * 0.05], bark, 5); }
  for (let k = 0; k < lumps; k++) { const a = (lumps === 4 ? k * Math.PI / 2 : k * TAU / lumps) + r() * 0.5; blob(g, 0.055 + r() * 0.01, k % 2 ? dark : leaf, Math.cos(a) * 0.04, 0.19 + r() * 0.015, Math.sin(a) * 0.04, 0.7, 1, r() * 9); }
  blob(g, 0.05, leaf, 0, 0.225, 0, 0.7, 1, r() * 9);
  // the curtains: narrow cones hung apex-down round the dome's rim, longer on the outside (73.2, the owner: "cone base extend
  // beyond foliage crown and the exposed bases look weird" — their bases now hang from inside the dome's underside)
  const curtains = n(14, 6, 3);
  for (let k = 0; k < curtains; k++) {
    const a = (k / curtains) * Math.PI * 2 + r() * 0.3, rad = 0.052 + r() * 0.01, h = 0.1 + r() * 0.05, top = 0.192 + r() * 0.01;
    const m = coneAt(g, 0.012 + r() * 0.003, h, k % 2 ? dark : leaf, Math.cos(a) * rad, top - h, Math.sin(a) * rad, 5, true);
    m.rotation.z = Math.cos(a) * 0.08; m.rotation.x = Math.PI - Math.sin(a) * 0.08;
  }
  return [];
};

/** Parasol thorn: an acacia — a slim trunk splitting into three leaning limbs under flat, layered pads. */
const parasolThorn: Build = (g, _c, r, n) => {
  const bark = '#6a5a48', leaf = '#7c8a48', dark = '#5e6a36', top: V3 = [0.008, 0.09, 0.004];
  // (73.2, the owner: "trunk is too wide compared to crown branch expansion": the trunk tapers to the limbs' width and the
  // limbs rise from inside its top, so the three flare out of it with no step at the waist)
  trunkChain(g, [[0, 0, 0], top], [[0.014, 0.0085]], () => bark, 6);
  const fork: V3 = [top[0] * 0.9, top[1] - 0.008, top[2] * 0.9];
  const limbs = n(3, 2);
  for (let k = 0; k < limbs; k++) {
    const a = (limbs === 3 ? k * 2.1 : k * TAU / limbs) + r() * 0.5, out = 0.06 + r() * 0.02, end: V3 = [Math.cos(a) * out, 0.19 + r() * 0.012, Math.sin(a) * out];
    seg(g, 0.0075, 0.005, fork, end, bark, 5);
    blob(g, 0.055 + r() * 0.01, k % 2 ? dark : leaf, end[0], end[1] + 0.012, end[2], 0.22, 1, r() * 9);
  }
  blob(g, 0.07, leaf, 0, 0.212, 0, 0.2, 1, r() * 9);
  blob(g, 0.04, dark, 0.01, 0.228, -0.01, 0.24, 0, r() * 9);
  return [];
};

/** Pillar redwood: a huge buttressed column, bare to two-thirds up, short drooping tiers above. */
const pillarRedwood: Build = (g, _c, r, n) => {
  const bark = '#7a4a34', dk = '#5e3828', leaf = '#3e5a3a', dark = '#2e4630';
  // (73.2, the owner: "trunk extending higher than foliage and is too thick at the top": it tapers to a point inside the top tiers)
  lathe(g, [[0.05, 0], [0.04, 0.02], [0.032, 0.06], [0.028, 0.15], [0.022, 0.26], [0.014, 0.33], [0.006, 0.38], [0.002, 0.4]], bark, 9).userData.trunk = true;
  for (let k = 0; k < 4; k++) { const a = (k / 4) * Math.PI * 2 + 0.3; seg(g, 0.014, 0.006, [Math.cos(a) * 0.045, 0, Math.sin(a) * 0.045], [Math.cos(a) * 0.022, 0.05, Math.sin(a) * 0.022], dk, 5); }
  let y = 0.24;
  const tiers = n(6, 4);
  for (let i = 0; i < tiers; i++) {
    const t = i / (tiers - 1), rr = 0.065 * (1 - t * 0.7) + 0.008, h = 0.05 - t * 0.012;
    const tier = new THREE.Group(); tier.position.set((r() - 0.5) * 0.008, y, (r() - 0.5) * 0.008); tier.rotation.set((r() - 0.5) * 0.1, r() * 3, (r() - 0.5) * 0.1); g.add(tier);
    cone(tier, rr, h, i % 2 ? dark : leaf, 0, 7);
    cone(tier, rr * 0.95, h * 0.25, shade(dark, 0.8), -h * 0.02, 7, true);
    y += h * (tiers === 6 ? 0.62 : 0.62 * 6 / tiers);
  }
  cone(g, 0.008, 0.035, leaf, y - 0.005, 5);
  return [];
};

/** Tree fern: a fibrous trunk with a shaggy foot and a crown of a dozen arching fronds, a fiddlehead at the heart. */
const treeFern: Build = (g, _c, r, n) => {
  const trunk = '#4a3a2c', greens = ['#5e8a3e', '#4e7634', '#68943e'];
  lathe(g, [[0.012, 0], [0.009, 0.02], [0.0075, 0.1], [0.0085, 0.16], [0.011, 0.178]], trunk, 7).userData.trunk = true;
  // (the shaggy foot; 73.2, the owner: "roots have gap in upper angle integrating into trunk" — each rises from inside the
  // trunk, so its upper side leaves the bark with no notch; too thin to be wood, as before)
  for (let k = 0; k < 5; k++) { const a = k * 1.3; seg(g, 0.003, 0.0015, [Math.cos(a) * 0.003, 0.034, Math.sin(a) * 0.003], [Math.cos(a) * 0.02, 0, Math.sin(a) * 0.02], shade(trunk, 0.8), 4); }
  // a dozen fronds: each a broad blade rising from the crown, then a second one drooping off its end
  const fronds = n(12, 6, 2);
  for (let i = 0; i < fronds; i++) {
    const f = new THREE.Group(); f.position.set(0, 0.176, 0); f.rotation.order = 'YXZ';
    f.rotation.y = (i / fronds) * Math.PI * 2 + r() * 0.3; f.rotation.x = 0.75 + r() * 0.4;
    const col = greens[i % 3];
    leafBlade(f, 0.028, 0.05, col, 0, 0.025, 0);
    const tip = new THREE.Group(); tip.position.set(0, 0.048, 0); tip.rotation.x = 0.55 + r() * 0.25; f.add(tip);
    leafBlade(tip, 0.022, 0.045, shade(col, 0.9), 0, 0.022, 0);
    g.add(f);
  }
  blob(g, 0.008, '#7a9a4a', 0, 0.183, 0, 1.2, 0, 3);   // (the fiddlehead)
  return [];
};

/** Stilt mangrove: a trunk held off the ground on arched prop roots, a dense glossy dome, aerial roots hanging. */
const stiltMangrove: Build = (g, _c, r, n) => {
  const bark = '#5a5040', leaf = '#3e6a42', dark = '#2e5434';
  seg(g, 0.015, 0.011, [0, 0.04, 0], [0.004, 0.14, 0], bark, 6).userData.trunk = true;
  // the prop roots: seven thin arches out of the trunk's lower third, down to the mud
  const roots = n(7, 4);
  for (let k = 0; k < roots; k++) {
    const a = (k / roots) * Math.PI * 2 + r() * 0.4, y0 = 0.045 + r() * 0.045, out = 0.055 + r() * 0.02;
    trunkChain(g, [[Math.cos(a) * 0.012, y0, Math.sin(a) * 0.012], [Math.cos(a) * out * 0.5, y0 + 0.006, Math.sin(a) * out * 0.5], [Math.cos(a) * out * 0.85, y0 * 0.45, Math.sin(a) * out * 0.85], [Math.cos(a) * out, 0, Math.sin(a) * out]],
      [[0.0036, 0.0032], [0.0032, 0.003], [0.003, 0.0028]], () => shade(bark, 0.9), 4, false);
  }
  for (let k = 0; k < 4; k++) { const a = k * Math.PI / 2 + r() * 0.5; blob(g, 0.05 + r() * 0.01, k % 2 ? dark : leaf, Math.cos(a) * 0.045, 0.17 + r() * 0.015, Math.sin(a) * 0.045, 0.72, 1, r() * 9); }
  blob(g, 0.055, leaf, 0, 0.205, 0, 0.72, 1, r() * 9);
  for (let k = 0; k < 3; k++) { const a = k * 2.1 + r(), x = Math.cos(a) * 0.06, z = Math.sin(a) * 0.06; seg(g, 0.0018, 0.0015, [x, 0.15, z], [x * 1.05, 0.06 + r() * 0.03, z * 1.05], shade(bark, 1.1), 4); }   // (aerial roots)
  return [];
};

/** Dragon tree: a stout trunk forking and forking again into an upturned umbrella, its underside dark. */
const dragonBlood: Build = (g, _c, r, n) => {
  const bark = '#8a7a68', leaf = '#4a6a3a', dark = '#2e4026';
  seg(g, 0.024, 0.02, [0, 0, 0], [0, 0.11, 0], bark, 7).userData.trunk = true;
  const arms = n(4, 3);
  for (let k = 0; k < arms; k++) {
    const a = (arms === 4 ? k * Math.PI / 2 : k * TAU / arms) + r() * 0.4, mid: V3 = [Math.cos(a) * 0.04, 0.15, Math.sin(a) * 0.04];
    seg(g, 0.012, 0.009, [0, 0.105, 0], mid, bark, 6);
    // (73.2, the owner: the smallest branches "don't integrate very nicely into foliage cap": they end inside it, thinner)
    for (const s of [-0.45, 0.45]) { const b = a + s, end: V3 = [Math.cos(b) * 0.062, 0.205 + r() * 0.006, Math.sin(b) * 0.062]; seg(g, 0.0075, 0.0045, mid, end, bark, 5); }
  }
  blob(g, 0.105, leaf, 0, 0.215, 0, 0.3, 1, r() * 9);
  cone(g, 0.1, 0.028, dark, 0.18, 9, true);   // (the umbrella's dark underside)
  return [];
};

/** Candle cypress: a tall narrow spindle of dark green on a short trunk. */
const candleCypress: Build = (g, _c, r) => {
  const bark = '#4a3c30', leaf = '#3a5434', dark = '#2c4428';
  seg(g, 0.012, 0.009, [0, 0, 0], [0, 0.045, 0], bark, 6).userData.trunk = true;
  blob(g, 0.045, dark, 0, 0.12, 0, 2.0, 1, r() * 9);
  blob(g, 0.036, leaf, 0.004, 0.215, -0.003, 2.1, 1, r() * 9);
  blob(g, 0.021, dark, -0.002, 0.29, 0.002, 2.4, 0, r() * 9);
  cone(g, 0.01, 0.04, leaf, 0.32, 5);
  return [];
};

/** Gold larch: a sparse conifer in autumn gold — whorls of short drooping limbs with tufts, the trunk seen through. */
const goldLarch: Build = (g, _c, r, n) => {
  const bark = '#5a4434', gold = ['#c8a040', '#b08a30', '#d4b050'];
  seg(g, 0.013, 0.004, [0, 0, 0], [0, 0.3, 0], bark, 6).userData.trunk = true;
  const whorls = n(5, 3);
  for (let i = 0; i < whorls; i++) {
    const t = i / (whorls - 1), y = 0.075 + (whorls === 5 ? i * 0.045 : i * 0.18 / (whorls - 1)), rr = 0.075 * (1 - t * 0.7);
    for (let k = 0; k < 3; k++) {
      const a = k * 2.1 + i * 0.9 + r() * 0.4, end: V3 = [Math.cos(a) * rr, y - 0.012, Math.sin(a) * rr];
      seg(g, 0.003, 0.0018, [0, y, 0], end, bark, 4);
      blob(g, 0.02 - t * 0.006, gold[(i + k) % 3], end[0] * 0.85, end[1] - 0.002, end[2] * 0.85, 0.55, 0, r() * 9);
    }
  }
  blob(g, 0.016, gold[2], 0, 0.3, 0, 1.4, 0, r() * 9);
  return [];
};

/** Krummholz: a wind-sheared alpine pine — a twisted trunk crawling leeward, a low flagged mat crown. */
const krummholz: Build = (g, _c, r, n) => {
  const bark = '#4e4034', leaf = '#3a5640', dark = '#2c4432', s = (r() - 0.5) * 0.01;
  trunkChain(g, [[0, 0, 0], [0.02, 0.035, 0.005 + s], [0.05, 0.055, s], [0.078, 0.064, -0.005 + s]], [[0.014, 0.011], [0.011, 0.009], [0.009, 0.007]], () => bark, 6);
  const mats = n(4, 2);
  for (let k = 0; k < mats; k++) blob(g, 0.04 + r() * 0.008 - k * 0.003, k % 2 ? dark : leaf, 0.035 + k * 0.022, 0.07 + r() * 0.01, (r() - 0.5) * 0.03, 0.45, 1, r() * 9);
  seg(g, 0.004, 0.0025, [0.03, 0.05, 0], [0.036, 0.13, 0.002], bark, 4);   // (the flag: one stem above the mat, its tufts all leeward)
  blob(g, 0.018, leaf, 0.05, 0.11, 0, 0.6, 0, r() * 9);
  blob(g, 0.014, dark, 0.048, 0.13, 0.003, 0.6, 0, r() * 9);
  return [];
};

/** Ghost cap: a giant pale fungus — a stem with a ring, a broad violet cap with dark gills, faint lit spots. */
const ghostCap: Build = (g, c, r, n) => {
  const stem = '#d8d2c0', cap = '#8a7a9a', gills = '#4e4458';
  lathe(g, [[0.03, 0], [0.02, 0.015], [0.016, 0.06], [0.016, 0.12], [0.02, 0.152]], stem, 8).userData.trunk = true;
  seg(g, 0.026, 0.024, [0, 0.108, 0], [0, 0.114, 0], shade(stem, 0.9), 8);   // (the ring)
  blob(g, 0.1, cap, 0, 0.17, 0, 0.38, 1, r() * 9);
  cone(g, 0.096, 0.024, gills, 0.145, 10, true);
  const spots = n(6, 2, 2);
  for (let k = 0; k < spots; k++) { const a = r() * Math.PI * 2, d = 0.02 + r() * 0.06; blob(g, 0.007, c.accent ?? '#bfe6ec', Math.cos(a) * d, 0.2 - d * 0.35, Math.sin(a) * d, 0.6, 0, k, 0.7); }
  return [];
};

// ---------------------------------------------------------------------------
// 421 (DECISIONS 420/421): the species sheet's five, as it drew them (design_sheets/tree_species.ts) — the biome's
// colours (the maple's reds its own, as the larch's gold). A limb that stands wholly overhead is drawn only
// (`userData.noWood`: treeShapeFit's wood fit leaves it out — nothing walks or shoots through a limb three people up);
// a trunk, a root, the elm's rising limb bases and the spreading oak's heavy first lengths are wood as the oak's are.
// ---------------------------------------------------------------------------

const drawnOnly = (m: THREE.Mesh): THREE.Mesh => { m.userData.noWood = true; return m; };

/** American elm: a short trunk opening into five limbs that rise steeply and arch out — a vase — the crown a high fan. */
const vaseElm: Build = (g, c, r, n) => {
  seg(g, 0.026, 0.02, [0, 0, 0], [0, 0.075, 0], c.bark, 7, { a: [0, 1, 0] }).userData.trunk = true;
  const limbs = n(5, 3), ends: V3[] = [];
  for (let k = 0; k < limbs; k++) {
    const a = k * TAU / limbs + r() * 0.4, at: V3 = [Math.cos(a) * 0.008, 0.07, Math.sin(a) * 0.008], d1 = dirOf(a, 2.6), d2 = dirOf(a, 0.9);
    const mid = add(at, d1, 0.11 + r() * 0.02), end = add(mid, d2, 0.075 + r() * 0.02);
    const bv = new THREE.Vector3(...d1).add(new THREE.Vector3(...d2)).normalize(), bis: V3 = [bv.x, bv.y, bv.z];
    seg(g, 0.011, 0.0075, at, mid, c.bark, 5, { b: bis });
    drawnOnly(seg(g, 0.0075, 0.004, mid, end, c.bark, 5, { a: bis }));
    blob(g, 0.04 + r() * 0.008, k % 2 ? c.leafDark : c.leaf, end[0], end[1] + 0.012, end[2], 0.7, 1, r() * 9);
    blob(g, 0.03, c.leaf, end[0] * 0.75, end[1] + 0.034, end[2] * 0.75, 0.7, 0, r() * 9);
    ends.push(end);
  }
  // the fan's two top lumps, each reached by a twig rising inward off a limb's end (the owner, 421: "floating greenery … with
  // no branch connecting it"); drawn only, as the limbs' tops are (treeShapeFit floatingLumps holds every variant to it)
  drawnOnly(seg(g, 0.005, 0.003, ends[0], [0.01, 0.262, -0.008], c.bark, 4));
  drawnOnly(seg(g, 0.005, 0.003, ends[Math.floor(limbs / 2)], [-0.015, 0.254, 0.012], c.bark, 4));
  blob(g, 0.045, c.leaf, 0.01, 0.275, -0.008, 0.62, 1, r() * 9);
  blob(g, 0.038, c.leafDark, -0.015, 0.265, 0.012, 0.62, 0, r() * 9);
  return [];
};

/** Japanese maple: a small tree (TREE_SCALE_RANGE 0.55–0.75) — three stems from a short trunk, flat layered pads of red leaves. */
const japaneseMaple: Build = (g, c, r, n) => {
  const bark = shade(c.bark, 0.85), reds = ['#a83a2c', '#8c2e24', '#c25a3a'];
  seg(g, 0.013, 0.011, [0, 0, 0], [0, 0.03, 0], bark, 6, { a: [0, 1, 0] }).userData.trunk = true;
  const stems = n(3, 2), mids: V3[] = [];
  for (let k = 0; k < stems; k++) {
    const a = k * TAU / stems + r() * 0.5, at: V3 = [Math.cos(a) * 0.004, 0.028, Math.sin(a) * 0.004], d1 = dirOf(a, 1.6), d2 = dirOf(a, 0.5);
    const mid = add(at, d1, 0.05 + r() * 0.01), end = add(mid, d2, 0.04 + r() * 0.01);
    const bv = new THREE.Vector3(...d1).add(new THREE.Vector3(...d2)).normalize(), bis: V3 = [bv.x, bv.y, bv.z];
    seg(g, 0.007, 0.005, at, mid, bark, 5, { b: bis });
    drawnOnly(seg(g, 0.005, 0.0025, mid, end, bark, 5, { a: bis }));
    blob(g, 0.042 + r() * 0.006, reds[k % 3], end[0] * 0.9, end[1] + 0.004, end[2] * 0.9, 0.32, 1, r() * 9);
    blob(g, 0.03, reds[(k + 1) % 3], end[0] * 1.15, end[1] - 0.012, end[2] * 1.15, 0.3, 0, r() * 9);
    mids.push(mid);
  }
  // the two middle pads, each reached by a twig rising off a stem's bend (421: no lump without a branch to it); drawn only
  drawnOnly(seg(g, 0.004, 0.002, mids[0], [0.004, 0.112, -0.004], bark, 4));
  drawnOnly(seg(g, 0.004, 0.002, mids[Math.floor(stems / 2)], [-0.006, 0.134, 0.005], bark, 4));
  blob(g, 0.045, reds[1], 0.004, 0.118, -0.004, 0.3, 1, r() * 9);
  blob(g, 0.03, reds[2], -0.006, 0.14, 0.005, 0.32, 0, r() * 9);
  return [];
};

/** Spreading oak: a massive short trunk on buttress roots, five heavy limbs reaching out nearly level, a crown wider than tall. */
const spreadingOak: Build = (g, c, r, n) => {
  const bark = shade(c.bark, 0.9);
  for (let i = 0; i < 4; i++) { const a = (i / 4) * TAU + 0.6; seg(g, 0.012, 0.005, [Math.cos(a) * 0.034, 0, Math.sin(a) * 0.034], [Math.cos(a) * 0.014, 0.04, Math.sin(a) * 0.04 * 0.35], bark, 5); }
  trunkChain(g, [[0, 0, 0], [0.005, 0.055, -0.003], [-0.003, 0.095, 0.004]], [[0.034, 0.027], [0.027, 0.02]], () => bark, 7);
  const limbs = n(5, 3), mids: V3[] = [];
  for (let k = 0; k < limbs; k++) {
    const a = k * TAU / limbs + r() * 0.5, up1 = 0.7 + r() * 0.3, up2 = 0.15 + r() * 0.2;
    const at: V3 = [Math.cos(a) * 0.012, 0.085, Math.sin(a) * 0.012], d1 = dirOf(a, up1), d2 = dirOf(a + (r() - 0.5) * 0.4, up2);
    const mid = add(at, d1, 0.06 + r() * 0.015), end = add(mid, d2, 0.075 + r() * 0.02);
    const bv = new THREE.Vector3(...d1).add(new THREE.Vector3(...d2)).normalize(), bis: V3 = [bv.x, bv.y, bv.z];
    seg(g, 0.014, 0.009, at, mid, bark, 5, { b: bis });
    drawnOnly(seg(g, 0.009, 0.005, mid, end, bark, 5, { a: bis }));
    blob(g, 0.046 + r() * 0.008, k % 2 ? c.leafDark : c.leaf, end[0], end[1] + 0.016, end[2], 0.6, 1, r() * 9);
    blob(g, 0.036, c.leafDark, end[0] * 0.7, end[1] + 0.03, end[2] * 0.7, 0.6, 0, r() * 9);
    blob(g, 0.03, c.leaf, end[0] * 1.15, end[1] - 0.004, end[2] * 1.15, 0.6, 0, r() * 9);
    mids.push(mid);
  }
  // the crown's two middle lumps, each reached by a twig rising off a limb's bend (421: no lump without a branch to it); drawn only
  drawnOnly(seg(g, 0.006, 0.0035, mids[0], [0.01, 0.185, -0.006], bark, 4));
  drawnOnly(seg(g, 0.006, 0.0035, mids[Math.floor(limbs / 2)], [-0.016, 0.2, 0.012], bark, 4));
  blob(g, 0.05, c.leaf, 0.01, 0.2, -0.006, 0.6, 1, r() * 9);
  blob(g, 0.04, c.leafDark, -0.016, 0.215, 0.012, 0.6, 0, r() * 9);
  return [];
};

/** Round oak: a taller trunk, the limbs shorter and rising, one big rounded crown. */
const roundOak: Build = (g, c, r, n) => {
  const bark = shade(c.bark, 0.9);
  trunkChain(g, [[0, 0, 0], [0.004, 0.09, 0], [0, 0.165, 0.003]], [[0.027, 0.021], [0.021, 0.014]], () => bark, 7);
  const limbs = n(4, 3);
  for (let k = 0; k < limbs; k++) {
    const a = k * TAU / limbs + 0.4 + r() * 0.5, end = add([0, 0.15, 0], dirOf(a, 1.1 + r() * 0.4), 0.075 + r() * 0.015);
    drawnOnly(seg(g, 0.011, 0.005, [0, 0.15, 0], end, bark, 5));
    blob(g, 0.05 + r() * 0.008, k % 2 ? c.leafDark : c.leaf, end[0] * 0.95, end[1] + 0.01, end[2] * 0.95, 0.85, 1, r() * 9);
  }
  for (let k = 0; k < 3; k++) { const a = k * 2.1 + r() * 0.5; blob(g, 0.052, k % 2 ? c.leaf : c.leafDark, Math.cos(a) * 0.04, 0.265 + r() * 0.01, Math.sin(a) * 0.04, 0.85, 1, r() * 9); }
  blob(g, 0.048, c.leaf, 0, 0.31, 0, 0.85, 1, r() * 9);
  return [];
};

/** Pin oak: one straight leader, limbs in tiers — the lowest drooping, the middle level, the top rising — a pyramid. */
const pinOak: Build = (g, c, r, n) => {
  const bark = shade(c.bark, 0.92);
  seg(g, 0.022, 0.005, [0, 0, 0], [0.002, 0.33, 0], bark, 7, { a: [0, 1, 0] }).userData.trunk = true;
  const tiers = n(4, 3);
  for (let i = 0; i < tiers; i++) {
    const t = i / (tiers - 1), y = 0.1 + t * 0.19, up = -0.35 + t * 1.3, len = 0.095 * (1 - t * 0.62) + 0.01, k0 = i === tiers - 1 ? 3 : 4;
    for (let k = 0; k < k0; k++) {
      const a = k * TAU / k0 + i * 0.8 + r() * 0.4, end = add([0, y, 0], dirOf(a, up), len);
      const m = seg(g, 0.008 - t * 0.003, 0.0035, [0, y, 0], end, bark, 4);
      if (i > 0) drawnOnly(m);   // (the lowest tier droops to a crouched head's height: wood; the rest stand overhead)
      blob(g, 0.03 + r() * 0.006 - t * 0.006, (i + k) % 2 ? c.leafDark : c.leaf, end[0] * 0.95, end[1] + 0.008, end[2] * 0.95, 0.7, 0, r() * 9);
      if (i < 2) blob(g, 0.024, c.leafDark, end[0] * 0.6, end[1] + 0.02 - up * 0.01, end[2] * 0.6, 0.7, 0, r() * 9);
    }
  }
  blob(g, 0.026, c.leaf, 0.002, 0.33, 0, 1.1, 0, r() * 9);
  return [];
};

// ---------------------------------------------------------------------------
// 423 (DECISIONS 423): the three redrawn on the species sheet for a silhouette difference, as it drew them
// ---------------------------------------------------------------------------

/** Quaking aspen: a narrow pale column — three clonal stems close together, the crown a slim spire of many small light lumps, a third of the birch's width and taller. */
const quakingAspen: Build = (g, c, r, n) => {
  const bark = shade(c.accent ?? '#d8d4c8', 0.98), leaf = shade(c.leaf, 1.22), light = shade(c.leaf, 1.4), dark = shade(c.leafDark, 1.1);
  const stems: Array<[number, number, number]> = [[0, 0, 0.4], [0.03, 0.012, 0.3], [-0.026, -0.016, 0.24]];   // (x, z, height)
  const main = n(11, 7, 2);
  stems.forEach(([sx, sz, h], si) => {
    const rad = 0.011 * h / 0.4;
    // (the main stem is the wood — 0.22 people across, as the birch's; the two suckers beside it are thinner than a rifle round
    // stops at and stand 0.6 people off it, so they are drawn only: otherwise the clump's walk disc swallowed the cover spots behind them)
    const stem = seg(g, rad, rad * 0.3, [sx, 0, sz], [sx + 0.002, h, sz], bark, 6, { a: [0, 1, 0] });
    if (si === 0) stem.userData.trunk = true; else drawnOnly(stem);
    for (let i = 0; i < 4; i++) { const t = 0.15 + i * 0.12 + r() * 0.04, a = r() * TAU; box(g, 0.006, 0.003, 0.003, '#4a4440', sx + Math.cos(a) * rad * (1 - 0.6 * t), t * h, sz + Math.sin(a) * rad * (1 - 0.6 * t), -a + Math.PI / 2); }
    // the spire: small lumps stacked up the top 55 % of the stem, never further out than 0.022 (the twigs too thin to be wood)
    const lumps = si === 0 ? main : 7;
    for (let k = 0; k < lumps; k++) {
      const t = k / (lumps - 1), y = h * (0.45 + 0.55 * t), out = 0.02 * Math.sin(Math.PI * (0.2 + 0.8 * t)) + 0.004, a = k * 2.4 + si + r() * 0.5;
      const end: V3 = [sx + Math.cos(a) * out, y, sz + Math.sin(a) * out];
      seg(g, 0.0025, 0.0015, [sx, y - 0.01, sz], end, bark, 4);
      blob(g, 0.013 + r() * 0.004 - t * 0.003, k % 3 === 0 ? dark : k % 3 === 1 ? leaf : light, end[0], end[1] + 0.004, end[2], 1.15, 0, r() * 9);
    }
    blob(g, 0.011, light, sx, h + 0.004, sz, 1.3, 0, r() * 9);
  });
  return [];
};

/** Weeping willow: a short stout trunk leaning over the water under a low wide mound whose curtains fall all the way to the ground — a hanging bell, not a dome. */
const weepingWillow: Build = (g, c, r, n) => {
  const bark = shade(c.bark, 0.95), leaf = '#a8b860', dark = '#80924a', pale = '#bccb74';
  const lean = 0.25 + r() * 0.15, p1: V3 = [Math.sin(lean) * 0.05, Math.cos(lean) * 0.05, 0], p2 = add(p1, [Math.sin(lean * 1.3) * 0.045, Math.cos(lean * 1.3) * 0.045, 0.008]);
  trunkChain(g, [[0, 0, 0], p1, p2], [[0.03, 0.024], [0.024, 0.017]], () => bark, 7);
  const top = add(p2, [0.015, 0.03, 0]);
  for (let k = 0; k < 4; k++) { const a = k * TAU / 4 + 0.4 + r() * 0.4; drawnOnly(seg(g, 0.011, 0.006, add(p2, [0, -0.006, 0]), add(top, dirOf(a, 0.3), 0.07), bark, 5)); }   // (the mound's limbs stand overhead: drawn only)
  // the mound: wide flat lumps low over the trunk, each curtain's base inside one (the crown rule, 421)
  for (let k = 0; k < 7; k++) { const a = k * TAU / 7 + r() * 0.4, rad = 0.06 + r() * 0.015; blob(g, 0.056 + r() * 0.01, k % 2 ? dark : leaf, top[0] + Math.cos(a) * rad, top[1] + 0.01 + r() * 0.01, top[2] + Math.sin(a) * rad, 0.55, 1, r() * 9); }
  blob(g, 0.06, leaf, top[0], top[1] + 0.035, top[2], 0.6, 1, r() * 9);
  blob(g, 0.04, pale, top[0] + 0.02, top[1] + 0.06, top[2] - 0.01, 0.6, 0, r() * 9);
  // the curtains: thin cones hung from the mound's underside to the ground, two rings so the bell is full
  const outer = n(22, 14, 4);
  for (const [cnt, rad0, w] of [[outer, 0.086, 0.011], [14, 0.058, 0.009]] as Array<[number, number, number]>) {
    for (let k = 0; k < cnt; k++) {
      const a = (k / cnt) * TAU + r() * 0.2, rad = rad0 + r() * 0.012, hang = top[1] + 0.012 + r() * 0.01, h = hang - 0.004;
      const m = coneAt(g, w + r() * 0.003, h, k % 3 === 0 ? pale : k % 2 ? dark : leaf, top[0] + Math.cos(a) * rad, hang - h, top[2] + Math.sin(a) * rad, 5, true);
      m.rotation.z = Math.cos(a) * 0.05; m.rotation.x = Math.PI - Math.sin(a) * 0.05;
    }
  }
  return [];
};

/** Chestnut: a huge spread — a thick short trunk and six heavy limbs reaching out low, the dome's skirt a crouch off the ground, nearly twice the puff elm's width. The limbs leave the trunk 1.6 people up and rise: above a standing head, so drawn only (wood a person cannot stand behind fed the bots' cover map with spots that protect nothing). */
const chestnut: Build = (g, c, r, n) => {
  const dark = shade(c.leafDark, 0.9), deep = shade(c.leafDark, 0.78);
  trunkChain(g, [[0, 0, 0], [0.004, 0.055, 0], [0, 0.1, 0.003]], [[0.036, 0.03], [0.03, 0.02]], () => c.bark, 7);
  const limbs = n(6, 4);
  for (let k = 0; k < limbs; k++) {
    const a = k * TAU / limbs + r() * 0.4, out = 0.13 + r() * 0.02, end = add([0, 0.085, 0], dirOf(a, 0.45 + r() * 0.2), out);
    drawnOnly(seg(g, 0.014, 0.007, [Math.cos(a) * 0.01, 0.08, Math.sin(a) * 0.01], end, c.bark, 5));
    // the skirt: big lumps at the limb's end, their undersides a crouch off the ground
    blob(g, 0.07 + r() * 0.01, k % 2 ? deep : dark, end[0] * 1.05, end[1] + 0.03, end[2] * 1.05, 0.72, 1, r() * 9);
    blob(g, 0.055, dark, end[0] * 0.7, end[1] + 0.07, end[2] * 0.7, 0.75, 0, r() * 9);
  }
  for (let k = 0; k < 5; k++) { const a = k * TAU / 5 + 0.6 + r() * 0.4, rr = 0.07; blob(g, 0.068 + r() * 0.008, k % 2 ? c.leafDark : dark, Math.cos(a) * rr, 0.215 + r() * 0.012, Math.sin(a) * rr, 0.78, 1, r() * 9); }
  blob(g, 0.065, c.leafDark, 0.006, 0.275, -0.004, 0.75, 1, r() * 9);
  blob(g, 0.045, c.leaf, -0.01, 0.315, 0.008, 0.75, 0, r() * 9);
  return [];
};

// ---------------------------------------------------------------------------
// 430 (DECISIONS 425 (3), 430): the rainforest sheet's three, as it drew them (design_sheets/rainforest.ts) — their own
// colours, as the tree fern's and the mangrove's. The kapok's limbs and the fig's stand seven and three people up: drawn only.
// ---------------------------------------------------------------------------

/** A buttress fin: a wedge standing on edge, its foot `out` from the axis at bearing `a`, rising to meet the trunk at `h` (a custom geometry: not wood, not a crown — drawn). */
function fin(parent: THREE.Object3D, a: number, out: number, h: number, thick: number, color: string): void {
  const c = Math.cos(a), s = Math.sin(a), nx = -s * thick / 2, nz = c * thick / 2;
  // the wedge's three corners (in the fin's vertical plane): the foot out, the foot at the axis, the top at the trunk
  const A: V3 = [c * out, 0, s * out], B: V3 = [c * 0.004, 0, s * 0.004], C: V3 = [c * 0.012, h, s * 0.012];
  const pos: number[] = [];
  const tri = (p: V3, q: V3, w: V3) => pos.push(...p, ...q, ...w);
  const off = (p: V3, k: number): V3 => [p[0] + nx * k, p[1], p[2] + nz * k];
  tri(off(A, 1), off(B, 1), off(C, 1)); tri(off(A, -1), off(C, -1), off(B, -1));   // the faces
  tri(off(A, -1), off(A, 1), off(C, 1)); tri(off(A, -1), off(C, 1), off(C, -1));   // the outer slope
  tri(off(B, 1), off(A, 1), off(A, -1)); tri(off(B, 1), off(A, -1), off(B, -1));   // the foot
  const g = new THREE.BufferGeometry(); g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3)); g.computeVertexNormals();
  parent.add(new THREE.Mesh(g, mat(color)));
}

/** Kapok (Ceiba): an emergent — a straight pale trunk on five buttress fins, bare to the top, then a flat wide umbrella of lumps on level limbs; the tree that stands over the roof. */
const kapok: Build = (g, _c, r, n) => {
  const bark = '#9a9080', dark = shade(bark, 0.8), leaf = '#4a7a3a', leafDark = '#355e2c', H = 0.36 + r() * 0.04;
  trunkChain(g, [[0, 0, 0], [0.004, H * 0.5, 0.002], [0.006, H + 0.012, 0.004]], [[0.021, 0.016], [0.016, 0.011]], i => i ? bark : dark, 7);
  const fins = 5;
  for (let k = 0; k < fins; k++) fin(g, (k / fins) * TAU + r() * 0.5, 0.045 + r() * 0.02, 0.07 + r() * 0.03, 0.005, dark);
  const limbs = n(5 + Math.floor(r() * 2), 3), top: V3 = [0.006, H, 0.004];
  for (let k = 0; k < limbs; k++) {
    const a = (k / limbs) * TAU + r() * 0.4, len = 0.07 + r() * 0.04, end = add(top, dirOf(a, 0.12 + r() * 0.1), len);
    drawnOnly(seg(g, 0.007, 0.0035, add(top, [0, -0.01 - r() * 0.015, 0]), end, bark, 5));   // (seven people up)
    blob(g, 0.05 + r() * 0.02, k % 2 ? leafDark : leaf, end[0], end[1] + 0.014, end[2], 0.4, 1, r() * 9);
  }
  blob(g, 0.055, leaf, top[0], top[1] + 0.022, top[2], 0.4, 1, r() * 9);
  return [];
};

/** Strangler fig (Ficus): a lattice of eight fused stems round a hollow core, twisting up and merging into a short trunk; four heavy limbs under a broad dense dome; aerial roots to the ground. */
const stranglerFig: Build = (g, _c, r, n) => {
  const bark = '#6a5a48', pale = shade(bark, 1.15), leaf = '#3e6e38', leafDark = '#2c5028';
  const stems = n(8, 5), merge = 0.1 + r() * 0.02;
  for (let k = 0; k < stems; k++) {
    const a = (k / stems) * TAU + r() * 0.3, a2 = a + 0.5 + r() * 0.4, r0 = 0.022 + r() * 0.006;
    trunkChain(g, [[Math.cos(a) * r0, 0, Math.sin(a) * r0], [Math.cos(a2) * 0.016, merge * 0.55, Math.sin(a2) * 0.016], [Math.cos(a2 + 0.3) * 0.008, merge, Math.sin(a2 + 0.3) * 0.008]],
      [[0.0075, 0.006], [0.006, 0.0055]], () => k % 2 ? bark : pale, 5);
  }
  seg(g, 0.024, 0.02, [0, merge - 0.01, 0], [0.004, merge + 0.05, 0], bark, 7).userData.trunk = true;
  const limbs = 4, top: V3 = [0.004, merge + 0.05, 0];
  for (let k = 0; k < limbs; k++) {
    const a = (k / limbs) * TAU + r() * 0.5, end = add(top, dirOf(a, 0.5 + r() * 0.3), 0.06 + r() * 0.02);
    drawnOnly(seg(g, 0.011, 0.006, top, end, bark, 5));   // (three people up)
    blob(g, 0.06 + r() * 0.015, k % 2 ? leafDark : leaf, end[0], end[1] + 0.02, end[2], 0.7, 1, r() * 9);
    // an aerial root from under the limb's end straight down to the ground, thin (under the wood's floor: drawn)
    if (k % 2 === 0) { const x = end[0] * 0.8, z = end[2] * 0.8; seg(g, 0.0025, 0.002, [x, end[1] - 0.005, z], [x * 1.1, 0, z * 1.1], pale, 4); }
  }
  blob(g, 0.07, leaf, top[0], top[1] + 0.04, top[2], 0.7, 1, r() * 9);
  return [];
};

/** Bamboo clump (Bambusa): a tight sheaf of a dozen jointed culms leaning out of one foot and arching at the top, leaf tufts at the upper joints — a screen, not a trunk (its culms are drawn only: concealment that blocks the walk, TREE_TRUNK_SIZE). */
const bambooClump: Build = (g, _c, r, n) => {
  const culm = '#b8b060', culmDark = shade(culm, 0.85), leaf = '#7aa84a', leafDark = '#5e8a3a';
  const culms = n(10 + Math.floor(r() * 4), 6, 2);
  for (let k = 0; k < culms; k++) {
    const a = (k / culms) * TAU + r() * 0.5, foot = 0.004 + r() * 0.008, lean = 0.03 + r() * 0.05, H = 0.2 + r() * 0.08;
    const pts: V3[] = [[Math.cos(a) * foot, 0, Math.sin(a) * foot]];
    for (let j = 1; j <= 3; j++) { const t = j / 3, out = foot + lean * t * t; pts.push([Math.cos(a) * out, H * t - (j === 3 ? 0.015 : 0), Math.sin(a) * out]); }
    const before = g.children.length;
    trunkChain(g, pts, [[0.0032, 0.0028], [0.0028, 0.0024], [0.0024, 0.0018]], i => i % 2 ? culm : culmDark, 5, false);
    for (let i = before; i < g.children.length; i++) drawnOnly(g.children[i] as THREE.Mesh);
    for (let j = 1; j <= 3; j++) {
      const p = pts[j], tufts = j === 3 ? 4 : 2;
      for (let t = 0; t < tufts; t++) { const b = r() * TAU; leafBlade(g, 0.006, 0.028, t % 2 ? leaf : leafDark, p[0] + Math.cos(b) * 0.006, p[1] + 0.01, p[2] + Math.sin(b) * 0.006, 0.9 + r() * 0.5, b); }
    }
  }
  return [];
};

const BUILD: Record<TreeFamily, Build> = {
  puffElm, canopyOak, paleBirch, spirePine, windPine, bottleTree, frondPalm, snag,
  drapeWillow, parasolThorn, pillarRedwood, treeFern, stiltMangrove, dragonBlood, candleCypress, goldLarch, krummholz, ghostCap,
  vaseElm, japaneseMaple, spreadingOak, roundOak, pinOak, quakingAspen, weepingWillow, chestnut,
  kapok, stranglerFig, bambooClump,
  stackFir: (g, c, r, n) => stackFir(g, c, r, false, n),
  lanternFir: (g, c, r, n) => stackFir(g, c, r, true, n),
};

/**
 * A tree of `family` in a biome's colours at scale 1, no yaw, base at y = 0, seeded (the stands' prototype seed). `steps`
 * (393): its main count moved by that many steps (its limbs, tiers, whorls, fronds or roots; the windswept pine and the
 * candle cypress have none) — 0, the stands' call, is the tree as before.
 */
export function buildFamilyTree(family: TreeFamily, colors: TreeColors, seed: number, steps = 0): { group: THREE.Group; sway: THREE.Object3D[] } {
  const g = new THREE.Group();
  const n: Count = (base, min = 1, step = 1) => steps === 0 ? base : Math.max(min, base + steps * step);
  const sway = BUILD[family](g, colors, rng(seed), n);
  return { group: g, sway };
}

// ---------------------------------------------------------------------------
// L1: light baked into the vertex colours (before the merge: still one geometry)
// ---------------------------------------------------------------------------

const CROWN_TYPES = new Set(['IcosahedronGeometry', 'ConeGeometry', 'PolyhedronGeometry', 'PlaneGeometry']);
const vmatCache = new Map<THREE.Material, THREE.MeshLambertMaterial>();
function vmat(from: THREE.MeshLambertMaterial): THREE.MeshLambertMaterial {
  let m = vmatCache.get(from);
  if (!m) { m = markShared(from.clone()); m.vertexColors = true; vmatCache.set(from, m); }
  return m;
}
/**
 * A crown's part darker at the crown's foot and toward its middle, lighter at its top (the light that gets in); the wood
 * darker toward the ground (0.72 at its foot, 1 a quarter of the tree up). Glowing parts are left as they are.
 */
export function bakeTreeLight(root: THREE.Object3D): void {
  root.updateMatrixWorld(true);
  const inv = new THREE.Matrix4().copy(root.matrixWorld).invert(), M = new THREE.Matrix4();
  const meshes: THREE.Mesh[] = [];
  root.traverse(o => { const m = o as THREE.Mesh; if (m.isMesh) meshes.push(m); });
  const v = new THREE.Vector3();
  const each = (m: THREE.Mesh, f: (v: THREE.Vector3, i: number) => void) => {
    M.multiplyMatrices(inv, m.matrixWorld);
    const pos = m.geometry.attributes.position as THREE.BufferAttribute;
    for (let i = 0; i < pos.count; i++) f(v.fromBufferAttribute(pos, i).applyMatrix4(M), i);
  };
  let lo = Infinity, hi = -Infinity, cx = 0, cz = 0, n = 0, top = 0;
  for (const m of meshes) {
    const crown = CROWN_TYPES.has(m.geometry.type);
    each(m, p => { top = Math.max(top, p.y); if (crown) { lo = Math.min(lo, p.y); hi = Math.max(hi, p.y); cx += p.x; cz += p.z; n++; } });
  }
  if (n) { cx /= n; cz /= n; }
  let reach = 1e-6;
  for (const m of meshes) if (CROWN_TYPES.has(m.geometry.type)) each(m, p => { reach = Math.max(reach, Math.hypot(p.x - cx, p.z - cz)); });
  for (const m of meshes) {
    const old = m.material as THREE.MeshLambertMaterial;
    if (old.emissive && old.emissive.getHex() !== 0) continue;
    const crown = CROWN_TYPES.has(m.geometry.type), col = new Float32Array(m.geometry.attributes.position.count * 3);
    each(m, (p, i) => {
      let k: number;
      if (crown) {
        const h = hi > lo ? (p.y - lo) / (hi - lo) : 1, out = Math.min(1, Math.hypot(p.x - cx, p.z - cz) / reach);
        k = (0.8 + 0.34 * h) * (0.9 + 0.14 * out);   // (about 1 on the mean: the crown keeps its colour, the light moves in it)
      } else k = 0.72 + 0.28 * Math.min(1, p.y / Math.max(1e-6, 0.25 * top));
      col[i * 3] = col[i * 3 + 1] = col[i * 3 + 2] = k;
    });
    m.geometry.setAttribute('color', new THREE.BufferAttribute(col, 3));
    m.material = vmat(old);
  }
}
